// src/utils/analytics.js
import { logEvent } from 'firebase/analytics';
import { analytics } from '../firebase/firebase';

/**
 * Base function to track events
 * Only logs if analytics is available
 */
export const trackEvent = (eventName, params = {}) => {
  try {
    if (analytics) {
      logEvent(analytics, eventName, params);
      console.log('📊 Analytics Event:', eventName, params);
    }
  } catch (error) {
    // Don't let analytics errors break the app
    console.error('Analytics error:', error);
  }
};

// ===== USER AUTHENTICATION EVENTS =====

export const trackLogin = (method) => {
  trackEvent('login', { method }); // method: 'email', 'google'
};

export const trackSignup = (method) => {
  trackEvent('sign_up', { method });
};

export const trackLogout = () => {
  trackEvent('logout');
};

// ===== CASE MANAGEMENT EVENTS =====

export const trackCaseCreated = (caseId) => {
  trackEvent('case_created', { case_id: caseId });
};

export const trackCaseOpened = (caseId) => {
  trackEvent('case_opened', { case_id: caseId });
};

export const trackCaseEdited = (caseId) => {
  trackEvent('case_edited', { case_id: caseId });
};

export const trackCaseDeleted = (caseId) => {
  trackEvent('case_deleted', { case_id: caseId });
};

// ===== FILE UPLOAD EVENTS =====

export const trackFileUploadStarted = (fileCount, fileTypes) => {
  trackEvent('file_upload_started', {
    file_count: fileCount,
    file_types: fileTypes.join(',')
  });
};

export const trackFileUploadCompleted = (fileCount, durationMs) => {
  trackEvent('file_upload_completed', {
    file_count: fileCount,
    duration_ms: durationMs
  });
};

export const trackFileUploadFailed = (fileCount, errorMessage) => {
  trackEvent('file_upload_failed', {
    file_count: fileCount,
    error: errorMessage
  });
};

// ===== FILE MANAGEMENT EVENTS =====

export const trackFileReordered = (caseId, fileCount) => {
  trackEvent('files_reordered', {
    case_id: caseId,
    file_count: fileCount
  });
};

export const trackFileRenamed = (caseId) => {
  trackEvent('file_renamed', { case_id: caseId });
};

export const trackFileDeleted = (caseId) => {
  trackEvent('file_deleted', { case_id: caseId });
};

// ===== STAMP CONFIGURATION EVENTS =====

export const trackStampConfigChanged = (caseId, changes) => {
  trackEvent('stamp_config_changed', {
    case_id: caseId,
    changes: Object.keys(changes).join(',')
  });
};

export const trackStampPositionAdjusted = (caseId, fileId) => {
  trackEvent('stamp_position_adjusted', {
    case_id: caseId,
    file_id: fileId
  });
};

// ===== CONVERSION/PROCESSING EVENTS =====

export const trackConversionStarted = (fileType, fileId) => {
  trackEvent('conversion_started', {
    file_type: fileType,
    file_id: fileId
  });
};

export const trackConversionCompleted = (fileType, fileId, durationMs) => {
  trackEvent('conversion_completed', {
    file_type: fileType,
    file_id: fileId,
    duration_ms: durationMs
  });
};

export const trackConversionFailed = (fileType, fileId, errorMessage) => {
  trackEvent('conversion_failed', {
    file_type: fileType,
    file_id: fileId,
    error: errorMessage
  });
};

// ===== EXPORT EVENTS =====

export const trackExportStarted = (fileCount, caseId) => {
  trackEvent('export_started', {
    file_count: fileCount,
    case_id: caseId
  });
};

export const trackExportCompleted = (fileCount, durationMs, success) => {
  trackEvent('export_completed', {
    file_count: fileCount,
    duration_ms: durationMs,
    success: success
  });
};

export const trackExportFailed = (fileCount, errorMessage) => {
  trackEvent('export_failed', {
    file_count: fileCount,
    error: errorMessage
  });
};

// ===== PREVIEW EVENTS =====

export const trackFilePreviewOpened = (fileId, fileType) => {
  trackEvent('file_preview_opened', {
    file_id: fileId,
    file_type: fileType
  });
};

export const trackFilePreviewClosed = (fileId) => {
  trackEvent('file_preview_closed', { file_id: fileId });
};

// ===== ERROR TRACKING (supplement to Sentry) =====

export const trackError = (errorType, errorMessage, context = {}) => {
  trackEvent('error_occurred', {
    error_type: errorType,
    error_message: errorMessage.substring(0, 100), // Limit length
    ...context
  });
};

// ===== PERFORMANCE TRACKING =====

export const trackPerformance = (metricName, value, unit = 'ms') => {
  trackEvent('performance_metric', {
    metric_name: metricName,
    value: value,
    unit: unit
  });
};

// ===== DOCUMENT SET MANAGEMENT EVENTS =====

export const trackDocumentSetCreated = (setId, setName, hasCustomColor) => {
  trackEvent('document_set_created', {
    set_id: setId,
    set_name: setName,
    has_custom_color: hasCustomColor
  });
};

export const trackDocumentSetRenamed = (setId) => {
  trackEvent('document_set_renamed', {
    set_id: setId
  });
};

export const trackDocumentSetArchived = (setId, isArchived) => {
  trackEvent('document_set_archived', {
    set_id: setId,
    is_archived: isArchived
  });
};

export const trackDocumentSetDeleted = (setId) => {
  trackEvent('document_set_deleted', {
    set_id: setId
  });
};

export const trackDocumentSetReordered = (setCount, isBulk = false) => {
  trackEvent('document_set_reordered', {
    set_count: setCount,
    is_bulk: isBulk
  });
};

// ===== SINGLE DOCUMENT SET ASSIGNMENT EVENTS =====

export const trackDocumentAddedToSet = (setId, isNewSet = false) => {
  trackEvent('document_added_to_set', {
    set_id: setId,
    is_new_set: isNewSet
  });
};

export const trackDocumentRemovedFromSet = (setId) => {
  trackEvent('document_removed_from_set', {
    set_id: setId
  });
};

export const trackAllSetsClearedFromDocument = (documentId, clearedSetCount) => {
  trackEvent('all_sets_cleared_from_document', {
    document_id: documentId,
    cleared_set_count: clearedSetCount
  });
};

// ===== BULK DOCUMENT SET OPERATIONS =====

export const trackBulkSetsAddedToDocuments = (documentCount, setCount, newSetsCount = 0, durationMs) => {
  trackEvent('bulk_sets_added_to_documents', {
    document_count: documentCount,
    set_count: setCount,
    new_sets_count: newSetsCount,
    duration_ms: durationMs
  });
};

export const trackBulkSetsRemovedFromDocuments = (documentCount, setCount, durationMs) => {
  trackEvent('bulk_sets_removed_from_documents', {
    document_count: documentCount,
    set_count: setCount,
    duration_ms: durationMs
  });
};

export const trackBulkActionCancelled = (actionType) => {
  trackEvent('bulk_action_cancelled', {
    action_type: actionType // 'add' or 'remove'
  });
};

// ===== BULK SET UI INTERACTION EVENTS =====

export const trackBulkActionToolbarOpened = (selectedDocumentCount) => {
  trackEvent('bulk_action_toolbar_opened', {
    selected_document_count: selectedDocumentCount
  });
};

export const trackBulkSetSelectorOpened = (mode, availableSetCount) => {
  trackEvent('bulk_set_selector_opened', {
    mode: mode, // 'add' or 'remove'
    available_set_count: availableSetCount
  });
};

export const trackBulkSetSelectorSubmitted = (mode, selectedSetCount) => {
  trackEvent('bulk_set_selector_submitted', {
    mode: mode,
    selected_set_count: selectedSetCount
  });
};

export const trackBulkSetSelectorClosed = (submitted) => {
  trackEvent('bulk_set_selector_closed', {
    submitted: submitted
  });
};